import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function baseProps(size: number): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
}

export function IconHome({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

export function IconPaperAirplane({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
    </svg>
  );
}

export function IconDeviceMobile({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  );
}

export function IconPuzzle({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-2.695c.318-.344.679-.546 1.042-.652a2.12 2.12 0 0 0 1.415-.326A2.25 2.25 0 0 0 18 10.5V6a2.25 2.25 0 0 0-2.25-2.25h-4.5A2.25 2.25 0 0 0 9 6v1.5c0 .621-.504 1.125-1.125 1.125H6.75A2.25 2.25 0 0 0 4.5 10.5v.75m11.42-5.58-5.58 5.58M9 6.75h.008v.008H9V6.75Zm-3 3h.008v.008H6V9.75Z" />
    </svg>
  );
}

export function IconChartBarSquare({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
    </svg>
  );
}

export function IconBolt({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
    </svg>
  );
}

export function IconUserPlus({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
    </svg>
  );
}

export function IconUsers({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  );
}

/** Simple paw — outdoor animals / pets */
export function IconPaw({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M9.375 9.375a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm5.25 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0ZM6.375 5.25a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm10.5 0a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0ZM12 13.5c0 .828-.672 2.25-2.25 2.25S7.5 14.328 7.5 13.5c0-1.125.75-2.25 2.25-2.25S12 12.375 12 13.5Z" />
    </svg>
  );
}

/** Open book — learning / content analytics */
export function IconBookOpen({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

/** Chain link — hub resource tap */
export function IconLink({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
    </svg>
  );
}

/** Gift box — donations / rewards */
export function IconGift({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
    </svg>
  );
}

/** Sticker / face smiley */
export function IconSticker({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 10h.01M15 10h.01" />
      <path d="M8.5 14.5c1.2 1.5 2.8 2.25 3.5 2.25s2.3-.75 3.5-2.25" />
    </svg>
  );
}

/** Hamburger menu */
export function IconMenu({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

/** Close / X */
export function IconX({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

/** List rows — term-sheet list view */
export function IconList({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M8 6h12M8 12h12M8 18h12" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  );
}

/** Stacked albums — term-sheet flashcard view */
export function IconAlbums({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M5.25 7.5h13.5A1.5 1.5 0 0 1 20.25 9v9A1.5 1.5 0 0 1 18.75 19.5H5.25A1.5 1.5 0 0 1 3.75 18V9A1.5 1.5 0 0 1 5.25 7.5Z" />
      <path d="M7.5 4.5h9" />
    </svg>
  );
}

/** Chevron down — dropdown affordance */
export function IconChevronDown({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

/** Paint brush — map paint tool */
export function IconPaintBrush({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128Zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42" />
    </svg>
  );
}

/** Square — rect fill tool */
export function IconSquare({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M5.25 7.5A2.25 2.25 0 0 1 7.5 5.25h9a2.25 2.25 0 0 1 2.25 2.25v9a2.25 2.25 0 0 1-2.25 2.25h-9a2.25 2.25 0 0 1-2.25-2.25v-9Z" />
    </svg>
  );
}

/** Magnifying glass — inspect tool */
export function IconMagnifyingGlass({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

/** Eraser — map eraser tool */
export function IconEraser({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
      <path d="M22 21H7" />
      <path d="m5 11 9 9" />
    </svg>
  );
}

/** Four-way arrows — map move tool */
export function IconArrowsMove({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M12 3.75v16.5" />
      <path d="M3.75 12h16.5" />
      <path d="m8.25 7.5 3.75-3.75L15.75 7.5" />
      <path d="m8.25 16.5 3.75 3.75 3.75-3.75" />
      <path d="m16.5 8.25 3.75 3.75-3.75 3.75" />
      <path d="m7.5 8.25-3.75 3.75 3.75 3.75" />
    </svg>
  );
}

/** Stacked cards — Learning Hub */
export function IconCards({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M7.5 6.75h10.5A1.5 1.5 0 0 1 19.5 8.25v9A1.5 1.5 0 0 1 18 18.75H7.5A1.5 1.5 0 0 1 6 17.25v-9A1.5 1.5 0 0 1 7.5 6.75Z" />
      <path d="M4.5 8.25v-1.5A1.5 1.5 0 0 1 6 5.25h10.5" />
    </svg>
  );
}

/** Alphabet / Aa */
export function IconAbc({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M3.25 19 9 5.25 14.75 19" />
      <path d="M5.4 14.25h7.2" />
      <path d="M16.25 19V11.1A3.15 3.15 0 0 1 22.5 11.1V19" />
      <path d="M16.25 14.35H22.5" />
    </svg>
  );
}

/** Settings gear */
export function IconCog({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M9.594 3.424c.32-1.053 1.816-1.053 2.136 0a1.724 1.724 0 0 0 2.578 1.065c.94-.609 2.08.23 1.687 1.249a1.724 1.724 0 0 0 1.036 2.472c1.087.32 1.087 1.87 0 2.19a1.724 1.724 0 0 0-1.036 2.472c.393 1.02-.746 1.858-1.687 1.249a1.724 1.724 0 0 0-2.578 1.065c-.32 1.053-1.816 1.053-2.136 0a1.724 1.724 0 0 0-2.578-1.065c-.94.609-2.08-.23-1.687-1.249a1.724 1.724 0 0 0-1.036-2.472c-1.087-.32-1.087-1.87 0-2.19a1.724 1.724 0 0 0 1.036-2.472c-.393-1.02.746-1.858 1.687-1.249a1.724 1.724 0 0 0 2.578-1.065Z" />
      <circle cx="12" cy="12" r="2.25" />
    </svg>
  );
}

/** Globe — web app */
export function IconGlobe({ size = 16, ...p }: IconProps) {
  return (
    <svg {...baseProps(size)} {...p}>
      <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" />
      <path d="M3.6 9h16.8M3.6 15h16.8" />
      <path d="M12 3c2.5 2.6 3.8 5.8 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.8-3.8-9S9.5 5.6 12 3Z" />
    </svg>
  );
}

/** Heart — favorites */
export function IconHeart({ size = 16, filled = false, ...p }: IconProps & { filled?: boolean }) {
  return (
    <svg {...baseProps(size)} fill={filled ? 'currentColor' : 'none'} {...p}>
      <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
    </svg>
  );
}

